import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
} from '@mui/material';
import {
  AccountBalance,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Add,
  Send,
  RequestQuote,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useBanking } from '../context/BankingContext';
import { formatCurrency, formatDate, calculateCreditUtilization } from '../utils/mockData';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { accounts, transactions, creditCards, loans } = useBanking();
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  // Data is now filtered by user in BankingContext
  const userAccounts = accounts;
  const userTransactions = transactions
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 5);
  const userCreditCards = creditCards;
  const userLoans = loans;

  const totalBalance = userAccounts.reduce((sum, acc) => sum + acc.balance, 0);
  const totalCreditUsed = userCreditCards.reduce((sum, cc) => sum + cc.currentBalance, 0);
  const totalCreditLimit = userCreditCards.reduce((sum, cc) => sum + cc.creditLimit, 0);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <TrendingUp color="success" />;
      case 'transfer':
        return <Send color="primary" />;
      case 'withdrawal':
        return <TrendingDown color="error" />;
      case 'loan_disbursement':
        return <AccountBalance color="info" />;
      default:
        return <AccountBalance />;
    }
  };

  const getAccountTypeLabel = (type: string) => {
    switch (type) {
      case 'checking':
        return 'Checking';
      case 'savings':
        return 'Savings';
      case 'loan':
        return 'Loan';
      default:
        return type;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome back, {user.fullName}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Here's an overview of your financial accounts
        </Typography>
      </Box>

      {/* Account Overview */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Balance
              </Typography>
              <Typography variant="h4" color="primary">
                {formatCurrency(totalBalance)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Across {userAccounts.length} accounts
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Credit Utilization
              </Typography>
              <Typography variant="h4" color="secondary">
                {totalCreditLimit > 0 ? `${Math.round(calculateCreditUtilization(totalCreditUsed, totalCreditLimit))}%` : '0%'}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={totalCreditLimit > 0 ? calculateCreditUtilization(totalCreditUsed, totalCreditLimit) : 0}
                  color="secondary"
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatCurrency(totalCreditUsed)} of {formatCurrency(totalCreditLimit)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        
        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Loans
              </Typography>
              <Typography variant="h4" color="info.main">
                {userLoans.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {userLoans.length > 0 ? `Total: ${formatCurrency(userLoans.reduce((sum, loan) => sum + loan.currentBalance, 0))}` : 'No active loans'}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Quick Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/accounts')}
            >
              Open Account
            </Button>
            <Button
              variant="outlined"
              startIcon={<Send />}
              onClick={() => navigate('/accounts')}
            >
              Transfer Money
            </Button>
            <Button
              variant="outlined"
              startIcon={<CreditCard />}
              onClick={() => navigate('/credit-cards')}
            >
              Apply for Card
            </Button>
            <Button
              variant="outlined"
              startIcon={<RequestQuote />}
              onClick={() => navigate('/loans')}
            >
              Apply for Loan
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Accounts */}
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Accounts
              </Typography>
              <List>
                {userAccounts.map((account) => (
                  <ListItem key={account.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <AccountBalance color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${getAccountTypeLabel(account.accountType)} - ${account.accountNumber}`}
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{formatCurrency(account.balance)}</span>
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              backgroundColor: account.status === 'active' ? 'success.main' : 'error.main',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              textTransform: 'capitalize'
                            }}
                          >
                            {account.status}
                          </Typography>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                ))}
                {userAccounts.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No accounts found" />
                  </ListItem>
                )}
              </List>
              <Button
                variant="text"
                onClick={() => navigate('/accounts')}
                sx={{ mt: 1 }}
              >
                View All Accounts
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Recent Transactions */}
        <Box sx={{ flex: '1 1 400px', minWidth: '400px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Transactions
              </Typography>
              <List>
                {userTransactions.map((transaction) => (
                  <ListItem key={transaction.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      {getTransactionIcon(transaction.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={transaction.description}
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{formatDate(transaction.timestamp)}</span>
                          <span style={{ fontWeight: 'bold' }}>
                            {transaction.type === 'deposit' || transaction.type === 'loan_disbursement' ? '+' : '-'}
                            {formatCurrency(transaction.amount)}
                          </span>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                ))}
                {userTransactions.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No recent transactions" />
                  </ListItem>
                )}
              </List>
              <Button
                variant="text"
                onClick={() => navigate('/accounts')}
                sx={{ mt: 1 }}
              >
                View All Transactions
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Container>
  );
};

export default Dashboard; 